// The runtime bundle only contains echarts/core plus the charts/components
// registered below (roughly a third of the full library).
import type { ECharts, EChartsOption, GridComponentOption } from "echarts";

import { CustomChart, LineChart, ScatterChart } from "echarts/charts";
import {
  AxisPointerComponent,
  DataZoomComponent,
  GraphicComponent,
  GridComponent,
  LegendComponent,
  MarkAreaComponent,
  MarkLineComponent,
  TooltipComponent,
  VisualMapComponent,
} from "echarts/components";
import * as echarts from "echarts/core";
import { SVGRenderer } from "echarts/renderers";
import { useCallback, useEffect, useRef, useState } from "react";

// Everything any heartcore-chart-options builder uses. Adding a new option
// feature (e.g. markPoint) requires registering its component here, or the
// chart silently renders without it.
echarts.use([
  LineChart,
  ScatterChart,
  CustomChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  DataZoomComponent,
  AxisPointerComponent,
  VisualMapComponent,
  MarkLineComponent,
  MarkAreaComponent,
  GraphicComponent,
  SVGRenderer,
]);

export type { EChartsOption };

export type DataZoomRange = { start: number; end: number };

// Shortest pointer travel that counts as a zoom drag rather than a click. Kept
// above zrender's own 4px click threshold (Handler.js), so a gesture we treat
// as a drag is always one zrender has already refused to call a click — the
// click-triggered axis pointer can't fire mid-drag.
const DRAG_MIN_PX = 5;

// How close to 0–100 still counts as "the whole span". Matches the tolerance
// the zoom echo checks below already use.
const FULL_RANGE_EPS = 0.5;

// `getOption` always returns components as arrays, whatever the option set.
function chartGrids(chart: ECharts): GridComponentOption[] {
  const { grid } = chart.getOption();
  return Array.isArray(grid) ? (grid as GridComponentOption[]) : [];
}

// Vertical extent of one grid in container px, from whichever pair of
// `top`/`height`/`bottom` its builder set. Those are `number | string` — a
// percentage or `'auto'` falls back to the full height rather than guessing.
function gridTopBottom(g: GridComponentOption, chartH: number) {
  const top = typeof g.top === "number" ? g.top : 0;
  const bottom =
    typeof g.height === "number"
      ? top + g.height
      : typeof g.bottom === "number"
        ? chartH - g.bottom
        : chartH;
  return { top, bottom };
}

// Horizontal extent of one grid in container px, read the same way as the
// vertical one. All grids on a chart share the gutters, so the first is enough.
function gridLeftRight(g: GridComponentOption, chartW: number) {
  const left = typeof g.left === "number" ? g.left : 0;
  const right =
    typeof g.width === "number"
      ? left + g.width
      : typeof g.right === "number"
        ? chartW - g.right
        : chartW;
  return { left, right };
}

type PlotBounds = { top: number; bottom: number; left: number; right: number };

// The plot area as a whole — first grid's top to last grid's bottom, across the
// shared gutters. Below it sits the x-axis labels and the axis-pointer's drag
// handle, which the zoom drag must not swallow; outside it left and right lie
// the axis-name gutters, which the zoom band must not spill into.
function plotBounds(chart: ECharts): PlotBounds | null {
  const grids = chartGrids(chart);
  if (grids.length === 0) return null;
  const chartH = chart.getHeight();
  return {
    top: gridTopBottom(grids[0], chartH).top,
    bottom: gridTopBottom(grids[grids.length - 1], chartH).bottom,
    ...gridLeftRight(grids[0], chart.getWidth()),
  };
}

// Rubber-band time selection: drag inside the plot to pick a window, whose
// edges are reported in ms for the caller to convert and apply. Returns its own
// teardown. zrender handlers need no removal — chart.dispose() takes its zr
// with it.
function attachDragZoom(
  chart: ECharts,
  container: HTMLElement,
  onSelect: { current: ((startMs: number, endMs: number) => void) | undefined },
  onReset: { current: (() => void) | undefined },
  ruler: {
    value: { current: number | null | undefined };
    onChange: { current: ((axisValueMs: number | null) => void) | undefined };
  },
  currentZoom: { current: DataZoomRange | null },
): () => void {
  // Built on the first drag, so a chart that never uses the gesture (the zoom
  // bar) keeps its container untouched. A plain DOM child of the element
  // ECharts itself fills, whose children React never manages — not an ECharts
  // `graphic`, which would have to be id-merged against the ruler lines
  // applyRulerGraphics maintains, on every drag frame.
  let band: HTMLDivElement | null = null;
  let dragStartX: number | null = null;
  // Captured at mousedown rather than re-read per frame: the grid can't move
  // mid-drag, and both the band and the committed range clamp against it.
  let bounds: PlotBounds | null = null;

  // zrender captures the pointer, so a drag that leaves the chart keeps
  // reporting coordinates — past the gutters, and past the container itself.
  const clampX = (x: number) =>
    bounds ? Math.max(bounds.left, Math.min(bounds.right, x)) : x;

  // Bounds for the crosshair, cached on the chart's size. `plotBounds` goes
  // through getOption(), which clones the whole option — far too heavy to run
  // on every mousemove. Only the cursor reads this: the gesture itself still
  // takes fresh bounds at mousedown, so a stale frame here can at worst show
  // the wrong cursor for one move, never zoom the wrong range.
  let cursorBounds: PlotBounds | null = null;
  let cursorBoundsKey = "";
  const boundsForCursor = () => {
    const key = `${chart.getWidth()}x${chart.getHeight()}`;
    if (key !== cursorBoundsKey) {
      cursorBoundsKey = key;
      cursorBounds = plotBounds(chart);
    }
    return cursorBounds;
  };

  const placeBand = (b: PlotBounds) => {
    if (!band) {
      band = document.createElement("div");
      band.style.cssText =
        "position:absolute;display:none;pointer-events:none;" +
        // Theme-agnostic: mid-grey at low alpha reads on both light and dark.
        "background:rgba(128,128,128,0.25);z-index:1";
      container.style.position = "relative";
      container.appendChild(band);
    }
    // Match the plot, so the band stops at the axis line rather than running
    // down over the time labels and the ruler handle.
    band.style.top = `${b.top}px`;
    band.style.height = `${b.bottom - b.top}px`;
  };

  // Point tooltips trigger on mousemove, so a drag across a dense series drags a
  // tooltip along with it, over the band. Hiding the tip from our own mousemove
  // handler is a race against ECharts' — which runs on the same event and wins —
  // so switch the component off for the drag and back on when it ends.
  let tooltipOff = false;
  const suppressTooltip = (off: boolean) => {
    if (off === tooltipOff) return;
    // Charts with no tooltip of their own must not be given one on restore.
    const { tooltip } = chart.getOption();
    if (!Array.isArray(tooltip) || tooltip.length === 0) return;
    tooltipOff = off;
    chart.setOption({ tooltip: { show: !off } });
    if (off) chart.dispatchAction({ type: "hideTip" });
  };

  const endDrag = () => {
    dragStartX = null;
    bounds = null;
    if (band) band.style.display = "none";
    suppressTooltip(false);
  };

  // Where the ruler sat before a double-click began. The pair's FIRST click
  // moves it, and at the moment that click fires nothing distinguishes it from
  // a deliberate one — so the only way to leave the ruler where it was is to
  // put it back once the pair is known. A native listener because zrender's
  // event carries no click count.
  let rulerBeforeClick: number | null | undefined;
  const snapshotRuler = (e: MouseEvent) => {
    if (e.detail === 1) rulerBeforeClick = ruler.value.current;
  };
  container.addEventListener("mousedown", snapshotRuler);

  const zr = chart.getZr();
  zr.on("mousedown", (e: { offsetX: number; offsetY: number }) => {
    if (!onSelect.current) return;
    // Everything below the plot belongs to the axis-pointer's drag handle. A
    // press there is the user moving the ruler, so it must not also start a
    // zoom selection. Read per-press: zoom and resize both move the grid.
    const b = plotBounds(chart);
    if (!b || e.offsetY > b.bottom) return;
    bounds = b;
    // Clamped on the way in, so a press that started on the axis labels anchors
    // at the plot edge — and a gesture that never left the gutter stays
    // zero-width and is dropped below as a non-drag.
    dragStartX = clampX(e.offsetX);
    placeBand(b);
    suppressTooltip(true);
  });

  // zrender's own dblclick rather than one on the container, so the reset obeys
  // the same plot bounds as the drag: double-clicking the ruler handle is a
  // ruler gesture, not a zoom one.
  zr.on("dblclick", (e: { offsetY: number }) => {
    if (!onReset.current) return;
    const b = plotBounds(chart);
    if (!b || e.offsetY > b.bottom) return;
    // At full range there is no zoom to reset, so the pair would do nothing but
    // put the ruler back — and with no rescale happening to cover it, that read
    // as a visible jump. Ignore the double-click instead and let its first
    // click stand, which is all a click was ever going to do here.
    const z = currentZoom.current;
    if (!z || (z.start <= FULL_RANGE_EPS && z.end >= 100 - FULL_RANGE_EPS))
      return;
    onReset.current();
    // Undo the ruler move the pair's clicks caused. dblclick is the last event
    // of the sequence, so this is the final word on where the ruler lands.
    if (typeof rulerBeforeClick === "number")
      ruler.onChange.current?.(rulerBeforeClick);
  });

  zr.on("mousemove", (e: { offsetX: number; offsetY: number }) => {
    // Crosshair exactly where a press would start a zoom — the same test the
    // mousedown guard uses, so the cursor never promises a gesture the chart
    // would refuse. Written after zrender, which sets `cursor` inline on this
    // element earlier in the same event; below the plot we leave its value
    // alone, which is how the ruler handle keeps its own cursor.
    const viewport = container.firstElementChild as HTMLElement | null;
    if (viewport && onSelect.current) {
      const cb = bounds ?? boundsForCursor();
      if (cb && e.offsetY <= cb.bottom) viewport.style.cursor = "crosshair";
    }

    if (dragStartX === null || !band) return;
    const x = clampX(e.offsetX);
    const width = Math.abs(x - dragStartX);
    band.style.display = width < DRAG_MIN_PX ? "none" : "block";
    band.style.left = `${Math.min(dragStartX, x)}px`;
    band.style.width = `${width}px`;
  });

  // On window, not zrender: releasing outside the chart still has to commit the
  // drag, and zrender only sees what happens over its own viewport. `pointerup`
  // rather than `mouseup` so a touch drag commits too — no compatibility
  // mouse event follows a pan.
  const finishDrag = (event: PointerEvent) => {
    const from = dragStartX;
    const to = clampX(event.clientX - container.getBoundingClientRect().left);
    endDrag();
    if (from === null) return;
    // Under zrender's own 4px click threshold this was a click, not a drag:
    // leave it to the axis pointer, which places the ruler on click. Above it,
    // zrender has already suppressed the click, so the two never overlap.
    if (Math.abs(to - from) < DRAG_MIN_PX) return;
    const a = chart.convertFromPixel({ xAxisIndex: 0 }, Math.min(from, to));
    const b = chart.convertFromPixel({ xAxisIndex: 0 }, Math.max(from, to));
    if (typeof a !== "number" || typeof b !== "number") return;
    if (!Number.isFinite(a) || !Number.isFinite(b)) return;
    onSelect.current?.(a, b);
  };
  window.addEventListener("pointerup", finishDrag);
  // A touch that turns into a page scroll cancels rather than releasing, and
  // would otherwise leave the band painted on the chart.
  window.addEventListener("pointercancel", endDrag);

  return () => {
    window.removeEventListener("pointerup", finishDrag);
    window.removeEventListener("pointercancel", endDrag);
    container.removeEventListener("mousedown", snapshotRuler);
    band?.remove();
  };
}

// Persistent dashed ruler lines for grids without a drag handle — ECharts
// clears their native axis-pointer lines on mouse-leave, while graphics live
// outside its pointer state machine. Call whenever the x-mapping changes
// (value, zoom, resize). `force` opts single-grid charts in (Events bar).
function applyRulerGraphics(
  chart: ECharts,
  v: number,
  idsRef: { current: string[] },
  force = false,
  stroke = "#555",
): void {
  const rawGrids = chartGrids(chart);
  // Skip single-grid charts unless explicitly opted in. Those charts use a
  // native handle to keep the line alive; adding a graphic creates a duplicate.
  if (!force && rawGrids.length <= 1) return;

  const px = chart.convertToPixel({ xAxisIndex: 0 }, v);
  if (typeof px !== "number" || !Number.isFinite(px)) return;

  const chartH = chart.getHeight();
  const ids: string[] = [];
  // Multi-grid: draw on every grid except the last (which owns the handle).
  // Forced single-grid: draw on the one grid.
  const gridsToDraw = rawGrids.length > 1 ? rawGrids.slice(0, -1) : rawGrids;
  const lines = gridsToDraw.map((g, i) => {
    const id = `ruler-grid-${i}`;
    ids.push(id);
    const { top, bottom } = gridTopBottom(g, chartH);
    return {
      id,
      type: "line" as const,
      shape: { x1: px, y1: top, x2: px, y2: bottom },
      style: { stroke, lineWidth: 2, lineDash: [4, 3] },
      silent: true,
      z: 10,
    };
  });
  idsRef.current = ids;
  chart.setOption({ graphic: lines });
}

export function EChartsChart(props: {
  option: EChartsOption;
  className?: string;
  style?: React.CSSProperties;
  // Changing it re-initialises the chart; the state effects below re-apply
  // the current option/pointer/visibility/zoom to the fresh instance.
  overflowVisible?: boolean;
  // ---- shared axis pointer ------------------------------------------------
  // Drives the x-axis pointer to this timestamp (ms). Paired with
  // onAxisPointerUpdate to sync sibling charts without `echarts.connect()`,
  // which would also sync tooltips.
  axisPointerValue?: number | null;
  onAxisPointerUpdate?: (axisValueMs: number | null) => void;
  // ---- shared zoom range (percentages 0–100) ------------------------------
  dataZoomRange?: DataZoomRange | null;
  onDataZoomChange?: (range: DataZoomRange) => void;
  // ---- drag-to-zoom -------------------------------------------------------
  // Drag a horizontal band across the plot to pick a time window; its edges are
  // reported in ms. The caller owns the time domain, so it converts to the
  // percentages the dataZoom pipeline speaks. Inert unless passed.
  onZoomSelect?: (startMs: number, endMs: number) => void;
  // Double-click anywhere on the plot — the reset half of the same gesture.
  onZoomReset?: () => void;
  // ---- external series-visibility control --------------------------------
  // Maps series name → visible. Applied via legendSelect/legendUnSelect, so
  // the chart option must include a `legend` component (visible or not).
  selectedSeries?: Record<string, boolean>;
  // Opts a single-grid chart with no drag handle (e.g. the Events bar) into
  // the persistent graphic ruler — see applyRulerGraphics.
  persistGraphicRuler?: boolean;
  // Stroke for the graphic ruler. Pass the option's native axis-pointer colour
  // so the two read identically (the default grey vanishes on dark themes).
  rulerColor?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<ECharts | null>(null);
  // Also held as state so the apply-effects below key on it: a re-init hands
  // them a new identity and they re-apply option/pointer/visibility/zoom —
  // with only the ref, the chart would come back blank until a prop changed.
  const [chartInstance, setChartInstance] = useState<ECharts | null>(null);

  const axisPointerCallbackRef = useRef(props.onAxisPointerUpdate);
  axisPointerCallbackRef.current = props.onAxisPointerUpdate;
  const dataZoomCallbackRef = useRef(props.onDataZoomChange);
  dataZoomCallbackRef.current = props.onDataZoomChange;

  // Always-current copies for use inside stable callbacks (ResizeObserver,
  // datazoom event) that can't depend on the React render cycle.
  const axisPointerValueRef = useRef(props.axisPointerValue);
  axisPointerValueRef.current = props.axisPointerValue;
  const persistGraphicRulerRef = useRef(props.persistGraphicRuler);
  persistGraphicRulerRef.current = props.persistGraphicRuler;
  const zoomSelectRef = useRef(props.onZoomSelect);
  zoomSelectRef.current = props.onZoomSelect;
  const zoomResetRef = useRef(props.onZoomReset);
  zoomResetRef.current = props.onZoomReset;
  const rulerColorRef = useRef(props.rulerColor);
  rulerColorRef.current = props.rulerColor;

  // Loop-prevention refs: when we programmatically dispatch
  // updateAxisPointer / dataZoom, ECharts re-fires the matching event. For
  // axisPointer we count expected echoes (multi-grid charts dispatch once
  // per xAxis); for zoom we compare values.
  const axisPointerEchoCountRef = useRef(0);
  const lastDispatchedZoomRef = useRef<DataZoomRange | null>(null);
  // The last range this chart emitted from a user drag. When the parent lifts it
  // and feeds it back via `dataZoomRange`, we must NOT re-dispatch it — doing so
  // fights the in-progress drag and makes the slider jitter back and forth.
  const lastEmittedZoomRef = useRef<DataZoomRange | null>(null);
  // Whatever range this chart currently shows, from any source (user drag,
  // external dispatch). Re-applied after a notMerge setOption, which would
  // otherwise reset the zoom to the full range (e.g. on a theme toggle).
  const currentZoomRef = useRef<DataZoomRange | null>(null);
  const rulerGraphicIdsRef = useRef<string[]>([]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const chart = echarts.init(container, undefined, { renderer: "svg" });
    chartRef.current = chart;
    setChartInstance(chart);

    if (props.overflowVisible) {
      const viewport = container.firstElementChild as HTMLElement | null;
      if (viewport) viewport.style.overflow = "visible";
    }

    chart.on("updateAxisPointer", (event: unknown) => {
      // Skip echoes from our own programmatic dispatches (one per xAxis in
      // multi-grid charts). Skipping here prevents both a tooltip-flicker
      // cascade and a re-emit loop back to the parent.
      if (axisPointerEchoCountRef.current > 0) {
        axisPointerEchoCountRef.current--;
        return;
      }
      // Suppress any tooltip ECharts may auto-show while the user is moving
      // the axis pointer (handle drag, click reposition). The per-point hover
      // tooltip re-shows on mousemove afterward.
      chart.dispatchAction({ type: "hideTip" });
      const e = event as { axesInfo?: { axisDim?: string; value?: number }[] };
      const xInfo = e.axesInfo?.find((a) => a.axisDim === "x");
      const v = xInfo?.value;
      // Don't emit null (e.g. on mouse-leave): axes without a handle fire leave
      // events even when a sibling handle-axis is holding its position. Emitting
      // null would clear the shared ruler state and make those lines disappear.
      if (typeof v !== "number") return;
      axisPointerCallbackRef.current?.(v);
    });

    chart.on("datazoom", (event: unknown) => {
      const e = event as {
        start?: number;
        end?: number;
        batch?: { start?: number; end?: number }[];
      };
      const first = e.batch?.[0] ?? e;
      const start = typeof first.start === "number" ? first.start : null;
      const end = typeof first.end === "number" ? first.end : null;
      if (start === null || end === null) return;
      // Track the shown range regardless of source (drag or echo) so it can be
      // restored after a notMerge option rebuild.
      currentZoomRef.current = { start, end };
      const last = lastDispatchedZoomRef.current;
      if (
        last &&
        Math.abs(last.start - start) < 0.5 &&
        Math.abs(last.end - end) < 0.5
      ) {
        lastDispatchedZoomRef.current = null;
        return;
      }
      lastEmittedZoomRef.current = { start, end };
      dataZoomCallbackRef.current?.({ start, end });
    });

    const detachDragZoom = attachDragZoom(
      chart,
      container,
      zoomSelectRef,
      zoomResetRef,
      { value: axisPointerValueRef, onChange: axisPointerCallbackRef },
      currentZoomRef,
    );

    const observer = new ResizeObserver(() => {
      chart.resize();
      // After resize the x-coordinate mapping changes — reposition any graphic
      // ruler lines so they stay aligned with their timestamp.
      const v = axisPointerValueRef.current;
      if (v != null)
        applyRulerGraphics(
          chart,
          v,
          rulerGraphicIdsRef,
          persistGraphicRulerRef.current,
          rulerColorRef.current,
        );
    });
    observer.observe(container);

    return () => {
      observer.disconnect();
      detachDragZoom();
      chart.dispose();
      chartRef.current = null;
    };
  }, [props.overflowVisible]);

  // Drive the native axis pointer(s) + graphic ruler lines to timestamp `v`.
  // Shared by the axisPointerValue effect and the option effect below — a
  // notMerge setOption resets ECharts' pointer state and graphics, so the
  // ruler must be re-applied after every option rebuild. Only refs inside, so
  // the callback is stable.
  const applyAxisPointer = useCallback((chart: ECharts, v: number) => {
    // Draw/reposition graphic ruler lines for non-handle grids (multi-grid only).
    applyRulerGraphics(
      chart,
      v,
      rulerGraphicIdsRef,
      persistGraphicRulerRef.current,
      rulerColorRef.current,
    );

    const px = chart.convertToPixel({ xAxisIndex: 0 }, v);
    if (typeof px !== "number" || !Number.isFinite(px)) return;

    const opt = chart.getOption() as { xAxis?: unknown };
    const xAxisCount = Array.isArray(opt.xAxis) ? opt.xAxis.length : 1;
    const h = chart.getHeight();
    axisPointerEchoCountRef.current = xAxisCount;
    for (let i = 0; i < xAxisCount; i++) {
      const yI = (h / xAxisCount) * (i + 0.5);
      chart.dispatchAction({
        type: "updateAxisPointer",
        currTrigger: "click",
        x: px,
        y: yI,
      });
    }
  }, []);

  useEffect(() => {
    const chart = chartInstance;
    if (!chart) return;
    // Synchronous (no lazyUpdate): the re-applies below need the new option's
    // coordinate system to be in effect.
    chart.setOption(props.option, { notMerge: true });
    // notMerge resets dataZoom, axis-pointer state and graphic rulers. Restore
    // the current interaction state so an option rebuild (theme toggle, data
    // refresh) doesn't visibly reset the zoom window or the shared ruler.
    const zoom = currentZoomRef.current;
    if (zoom) {
      lastDispatchedZoomRef.current = zoom;
      chart.dispatchAction({
        type: "dataZoom",
        start: zoom.start,
        end: zoom.end,
        dataZoomIndex: 0,
      });
    }
    const v = axisPointerValueRef.current;
    if (v != null) applyAxisPointer(chart, v);
  }, [chartInstance, props.option, applyAxisPointer]);

  // Apply external axis pointer value.
  useEffect(() => {
    const chart = chartInstance;
    const v = props.axisPointerValue;
    if (!chart) return;

    if (v == null) {
      axisPointerEchoCountRef.current = 0;
      chart.dispatchAction({ type: "updateAxisPointer", currTrigger: "leave" });
      const ids = rulerGraphicIdsRef.current;
      if (ids.length > 0) {
        chart.setOption({
          graphic: ids.map((id) => ({ id, $action: "remove" as const })),
        });
        rulerGraphicIdsRef.current = [];
      }
      return;
    }

    applyAxisPointer(chart, v);
  }, [chartInstance, props.axisPointerValue, applyAxisPointer]);

  // Apply external series-visibility map.
  useEffect(() => {
    const chart = chartInstance;
    const sel = props.selectedSeries;
    if (!chart || !sel) return;
    for (const [name, visible] of Object.entries(sel)) {
      chart.dispatchAction({
        type: visible ? "legendSelect" : "legendUnSelect",
        name,
      });
    }
  }, [chartInstance, props.selectedSeries, props.option]);

  // Apply external zoom range.
  useEffect(() => {
    const chart = chartInstance;
    const r = props.dataZoomRange;
    if (!chart || !r) return;
    // Ignore the value echoing back from this chart's own drag — it's already
    // there, and re-dispatching mid-drag causes the slider to jump/jitter.
    const emitted = lastEmittedZoomRef.current;
    if (
      emitted &&
      Math.abs(emitted.start - r.start) < 0.5 &&
      Math.abs(emitted.end - r.end) < 0.5
    ) {
      return;
    }
    lastDispatchedZoomRef.current = r;
    currentZoomRef.current = r;
    chart.dispatchAction({
      type: "dataZoom",
      start: r.start,
      end: r.end,
      dataZoomIndex: 0,
    });
    // Zoom changes the x-coordinate mapping — reposition graphic ruler lines.
    const v = axisPointerValueRef.current;
    if (v != null)
      applyRulerGraphics(
        chart,
        v,
        rulerGraphicIdsRef,
        persistGraphicRulerRef.current,
        rulerColorRef.current,
      );
  }, [chartInstance, props.dataZoomRange]);

  return (
    <div ref={containerRef} className={props.className} style={props.style} />
  );
}
